import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { Message, PushBySender, RetrievePushBySender } from './messages.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { HttpService } from '../../../core/http/http.service';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';

@Injectable({
    providedIn: 'root'
})
export class MessagesService {
    lastTimestamp: string;
    public readonly sendNotificationUri = 'dtype://com.cloudcodice.service.pushnotification.worker.SendNotification';

    private _messages: BehaviorSubject<PushBySender[]> = new BehaviorSubject<PushBySender[]>([]);
    private messages: PushBySender[] = [];
    private _openPanel: ReplaySubject<any> = new ReplaySubject<any>(1);
    private _closePanel: ReplaySubject<void> = new ReplaySubject<void>(1);
    private _goToList: ReplaySubject<void> = new ReplaySubject<void>(1);
    private _messagesFromTask: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * Constructor
     */
    constructor(
        private dialogsService: DialogsService,
        private httpService: HttpService,
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for messages
     */
    get messages$(): Observable<PushBySender[]> {
        return this._messages.asObservable();
    }

    /**
     * Getter for openPanel
     */
    get openPanel$(): Observable<any> {
        return this._openPanel.asObservable();
    }

    /**
     * Getter for closePanel
     */
    get closePanel$(): Observable<any> {
        return this._closePanel.asObservable();
    }

    /**
     * Getter for goToList
     */
     get goToList$(): Observable<any> {
        return this._goToList.asObservable();
    }

    /**
     * Getter for messagesFromTask
     */
     get messagesFromTask$(): Observable<boolean> {
        return this._messagesFromTask.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get first messages
     */
    getFirst(filters?: { ackPending?: boolean; rcPending?: boolean }) {
        return this.retrievePushNotificationBySender(null, filters).pipe(
            map((notifications) => {
                this.messages = notifications;
                this._messages.next(notifications);
            }),
            catchError((error) => {
                console.error(error);
                return of(null);
            })
        );
    }

    get(filters?: { ackPending: boolean; rcPending: boolean }) {
        return this.retrievePushNotificationBySender(this.lastTimestamp, filters).pipe(
            map((notifications) => {
                this.messages.push(...notifications);
                this._messages.next(this.messages);
            }),
            catchError((error) => {
                console.error(error);
                return of(null);
            })
        );
    }

    /**
     * Create a message
     *
     * @param message
     */
    create(message: Message): Observable<Message> {
        return this.httpService.post<Message>(NextMethodsEnum.sendNotification, message).pipe(
            catchError((error) => {
                this.dialogsService.openErrorDialog();
                return throwError(() => error);
            }));
    }

    /**
     * Open the message panel with an optional message
     *
     * @param mesage
     */
    openMessagePanel(message?: Message) {
        this._openPanel.next(message);
    }

    /**
     * Closes the message panel
     *
     * @param mesage
     */
    closeMessagePanel() {
        this._closePanel.next();
    }

    /**
     * Navigates to the list view of the panel
     */
    goToListView() {
        this._goToList.next();
    }

    /**
     * Setter for messagesFromTask
     */
    setMessagesFromTask(value: boolean) {
        this._messagesFromTask.next(value);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private retrievePushNotificationBySender(senderTimestamp?: string, filters?: { ackPending?: boolean; rcPending?: boolean }) {
        const chunkSize = 10;
        const body: any = {
            chunkSize,
        };
        if (senderTimestamp) {
            body.senderTimestamp = senderTimestamp;
        }
        if (filters) {
            body.ackPending = filters.ackPending;
            body.rcPending = filters.rcPending;
        }
        return this.httpService.post<RetrievePushBySender>(NextMethodsEnum.retrievePushNotificationBySender, body).pipe(
            map((response) => {
                if (response && response.notification) {
                    if (response.notificationSize > chunkSize) {
                        this.lastTimestamp = response.notification[response.notificationSize - 1].senderTimestamp;
                        response.notification.pop();
                    } else {
                        this.lastTimestamp = undefined;
                    }
                    response.notification.map((notification) => {
                        notification.partaker = response.partaker.userId[notification.userId];
                    });
                    return response.notification;
                } else {
                    return [];
                }
            })
        );
    }
}
